import React, { useState, useEffect } from 'react';
import { useToast } from '../hooks/useToast';
import { getQueueMetrics } from '../services/queue';
import { setTitle } from '../utils/environment';
import AdminGate from '../components/AdminGate';
import PageHeader from '../components/PageHeader';
import PageLoading from '../components/PageLoading';

const PERIODS = [
  { label: '6h', hours: 6 },
  { label: '12h', hours: 12 },
  { label: '24h', hours: 24 },
  { label: '7d', hours: 168 },
];

const QueueDashboard = () => {
  const { showError } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hours, setHours] = useState(24);
  const [forbidden, setForbidden] = useState(false);

  const loadData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const result = await getQueueMetrics(hours);
      setData(result);
      setForbidden(false);
    } catch (error) {
      if (error.response?.status === 403) {
        setForbidden(true);
      } else {
        showError(error.response?.data?.detail || 'Failed to load queue metrics');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setTitle('Queue · Read It Never');
    loadData();
  }, [hours]);

  if (loading && !data) {
    return <PageLoading message="Loading queue metrics..." />;
  }

  if (forbidden) {
    return (
      <AdminGate backTo="/settings" />
    );
  }

  const { current, throughput, history } = data || { current: {}, throughput: {}, history: [] };

  // Queue depth chart
  const maxPending = history.length > 0
    ? Math.max(...history.map(h => h.pending_count || 0), 1)
    : 1;

  // Throughput chart: compute completed per interval from consecutive completed_total deltas
  const throughputBars = [];
  for (let i = 1; i < history.length; i++) {
    const delta = (history[i].completed_total || 0) - (history[i - 1].completed_total || 0);
    throughputBars.push({
      recorded_at: history[i].recorded_at,
      completed: Math.max(delta, 0),
    });
  }
  const maxThroughput = throughputBars.length > 0
    ? Math.max(...throughputBars.map(t => t.completed), 1)
    : 1;

  const formatTime = (isoStr) => {
    const d = new Date(isoStr);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-clay-bg">
      <PageHeader
        title="Processing Queue"
        subtitle="Article processing velocity"
        backTo="/settings"
        backLabel="Back to settings"
        actions={
          <button
            onClick={() => loadData(true)}
            disabled={refreshing}
            className="btn-clay-secondary btn-clay-sm flex items-center"
          >
            <svg className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        }
      />

      <main className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
        {/* Period Selector */}
        <div className="mb-6 flex items-center space-x-2">
          <span className="text-sm text-secondary-text">Period:</span>
          {PERIODS.map((p) => (
            <button
              key={p.hours}
              type="button"
              onClick={() => setHours(p.hours)}
              aria-pressed={hours === p.hours}
              className={`pill-clay text-sm ${hours === p.hours ? 'pill-clay-active' : ''}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Summary Cards */}
        <section className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-amber-400 text-center">
              <p className="font-display text-4xl font-bold text-amber-500 mb-2">{current.pending ?? 0}</p>
              <p className="text-sm font-medium text-body-text">Pending</p>
              {current.oldest_pending_minutes != null && (
                <p className="text-xs text-secondary-text mt-1">oldest: {current.oldest_pending_minutes}m ago</p>
              )}
            </div>
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-primary-500 text-center">
              <p className="font-display text-4xl font-bold text-primary-600 mb-2">{current.processing ?? 0}</p>
              <p className="text-sm font-medium text-body-text">Processing</p>
              <p className="text-xs text-secondary-text mt-1">in-memory queue: {current.in_memory_queue_size ?? 0}</p>
            </div>
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-fun-mint text-center">
              <p className="font-display text-4xl font-bold text-fun-mint mb-2">{throughput.rate_per_minute ?? 0}</p>
              <p className="text-sm font-medium text-body-text">Articles/min</p>
              <p className="text-xs text-secondary-text mt-1">{throughput.completed_last_hour ?? 0} completed last hour</p>
            </div>
            <div className="card-clay-sm hover:shadow-clay-hover hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-fun-coral text-center">
              <p className="font-display text-4xl font-bold text-fun-coral mb-2">
                {throughput.estimated_drain_minutes != null
                  ? `${Math.round(throughput.estimated_drain_minutes)}m`
                  : current.pending === 0 ? 'Clear' : '—'}
              </p>
              <p className="text-sm font-medium text-body-text">Drain ETA</p>
              <p className="text-xs text-secondary-text mt-1">{throughput.failed_last_hour ?? 0} failed last hour</p>
            </div>
          </div>
        </section>

        {/* Queue Depth Chart */}
        <section className="mb-8">
          <h2 className="font-display text-lg font-semibold text-heading mb-4">Queue Depth</h2>
          <div className="card-clay p-6">
            {history.length > 0 ? (
              <>
                <div className="flex justify-between items-center mb-6">
                  <p className="text-lg font-semibold text-heading">Pending Articles Over Time</p>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-amber-400 rounded-clay mr-2"></div>
                    <span className="text-sm text-secondary-text">Pending</span>
                  </div>
                </div>
                <div className="flex items-end justify-between h-40 overflow-x-auto">
                  {history.map((point, idx) => {
                    const height = (point.pending_count / maxPending) * 100;
                    return (
                      <div key={idx} className="flex flex-col items-center min-w-[20px]">
                        <div className="h-32 flex items-end justify-center">
                          <div
                            className="w-2 bg-amber-400 rounded-t-clay transition-all duration-300"
                            style={{ height: `${height}%`, minHeight: point.pending_count > 0 ? '2px' : '0' }}
                            title={`${formatTime(point.recorded_at)}: ${point.pending_count} pending`}
                          ></div>
                        </div>
                        {/* Show every ~4th label to avoid crowding */}
                        {idx % Math.max(1, Math.floor(history.length / 12)) === 0 && (
                          <p className="text-xs text-secondary-text mt-2">{formatTime(point.recorded_at)}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="text-secondary-text text-center italic py-8">No snapshot data yet — snapshots are recorded every 5 minutes</p>
            )}
          </div>
        </section>

        {/* Throughput Chart */}
        <section className="mb-8">
          <h2 className="font-display text-lg font-semibold text-heading mb-4">Throughput</h2>
          <div className="card-clay p-6">
            {throughputBars.length > 0 ? (
              <>
                <div className="flex justify-between items-center mb-6">
                  <p className="text-lg font-semibold text-heading">Completions Per Interval</p>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-fun-mint rounded-clay mr-2"></div>
                    <span className="text-sm text-secondary-text">Completed</span>
                  </div>
                </div>
                <div className="flex items-end justify-between h-40 overflow-x-auto">
                  {throughputBars.map((bar, idx) => {
                    const height = (bar.completed / maxThroughput) * 100;
                    return (
                      <div key={idx} className="flex flex-col items-center min-w-[20px]">
                        <div className="h-32 flex items-end justify-center">
                          <div
                            className="w-2 bg-fun-mint rounded-t-clay transition-all duration-300"
                            style={{ height: `${height}%`, minHeight: bar.completed > 0 ? '2px' : '0' }}
                            title={`${formatTime(bar.recorded_at)}: ${bar.completed} completed`}
                          ></div>
                        </div>
                        {idx % Math.max(1, Math.floor(throughputBars.length / 12)) === 0 && (
                          <p className="text-xs text-secondary-text mt-2">{formatTime(bar.recorded_at)}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="text-secondary-text text-center italic py-8">Need at least 2 snapshots to show throughput</p>
            )}
          </div>
        </section>

        {/* Worker Status */}
        <section className="mb-8">
          <h2 className="font-display text-lg font-semibold text-heading mb-4">Workers</h2>
          <div className="card-clay p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-secondary-text">Active / Total</p>
                <p className="font-display text-3xl font-bold text-heading">
                  {current.workers_active ?? 0} / {current.workers_total ?? 0}
                </p>
              </div>
              {current.avg_processing_seconds != null && (
                <div className="text-right">
                  <p className="text-sm text-secondary-text">Avg Processing Time</p>
                  <p className="font-display text-3xl font-bold text-heading">
                    {current.avg_processing_seconds}s
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Totals */}
        <section className="mb-8">
          <div className="grid grid-cols-2 gap-6">
            <div className="card-clay-sm text-center">
              <p className="font-display text-3xl font-bold text-fun-mint">{(current.completed_total ?? 0).toLocaleString()}</p>
              <p className="text-sm text-secondary-text">Total Completed</p>
            </div>
            <div className="card-clay-sm text-center">
              <p className="font-display text-3xl font-bold text-fun-coral">{(current.failed_total ?? 0).toLocaleString()}</p>
              <p className="text-sm text-secondary-text">Total Failed</p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default QueueDashboard;
